import { Fragment, useState } from "react";
import { Box, Collapse, Divider, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "../../content/pages/homepage.module.css";
import ChartScatter from "../Charts/ChartScatter";
import ChartBar from "../Charts/ChartBar";
import ChartLine from "../Charts/ChartLine";
import ChartStackBar from "../Charts/ChartStackBar";
import { UserRequestHistoryItem } from "src/api/fetchUserRequestHistory";
import {
  formatHistoryTimestampLocal,
  getHistoryItemContent,
  normalizeHistoryCharts,
} from "src/utils/userRequestHistoryUi";

interface HistoryEndpointDetailProps {
  items: UserRequestHistoryItem[];
}

/** Same chart switch as WaiverResponseStream / Start / Rundown. */
const ChartRenderer = ({ chartData }: { chartData: any }) => {
  if (!chartData) return null;
  switch (chartData.chart_type) {
    case "scatter":
      return <ChartScatter chartData={chartData} />;
    case "bar":
      return <ChartBar chartData={chartData} />;
    case "stacked_bar":
      return <ChartStackBar chartData={chartData} />;
    case "line":
      return <ChartLine chartData={chartData} />;
    default:
      console.warn("Unknown chart type:", chartData.chart_type);
      return null;
  }
};

const HistoryTextBlock = ({ text }: { text: string }) => (
  <Box sx={{ px: 0 }}>
    <ReactMarkdown className={styles.reactMarkdownStart} remarkPlugins={[remarkGfm]}>
      {text}
    </ReactMarkdown>
  </Box>
);

/** Interleave markdown + charts by [CHART:id] placeholders (history dict → ordered array). */
const HistoryItemBody = ({ item }: { item: UserRequestHistoryItem }) => {
  const content = getHistoryItemContent(item);
  const charts = normalizeHistoryCharts(content, item.charts);
  const textSegments = content.split(/\[CHART:.*?\]|\[chart_\d+\]/gi);
  const chartPlaceholders = content.match(/\[CHART:.*?\]|\[chart_\d+\]/gi) || [];

  if (chartPlaceholders.length === 0) {
    return <HistoryTextBlock text={content} />;
  }

  return (
    <>
      {textSegments[0] && (
        <HistoryTextBlock text={textSegments[0]} />
      )}
      {chartPlaceholders.map((_, index) => {
        const chart = charts[index];
        const nextTextSegment = textSegments[index + 1];
        return (
          <Fragment key={`history-chart-${item.request_id}-${index}`}>
            {chart ? (
              <Box sx={{ py: 1, px: 0, width: "100%", overflowX: "auto", overflowY: "hidden" }}>
                <ChartRenderer chartData={chart} />
              </Box>
            ) : null}
            {nextTextSegment ? (
              <HistoryTextBlock text={nextTextSegment} />
            ) : null}
          </Fragment>
        );
      })}
    </>
  );
};

/** Timestamp accordion list — same expand pattern as FAQ questions, inverted colors. */
const HistoryEndpointDetail = ({ items }: HistoryEndpointDetailProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: "15px", color: "#ffffff", fontWeight: "bold" }}>
          No requests for this endpoint.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      sx={{
        p: 2,
        bgcolor: "#000000",
        userSelect: "text",
        WebkitUserSelect: "text",
      }}
      gap={0}
    >
      {items.map((item, index) => {
        const isExpanded = expandedId === item.request_id;
        const title = formatHistoryTimestampLocal(item.timestamp) || item.request_id;
        const isLast = index === items.length - 1;

        return (
          <Box key={item.request_id} sx={{ width: "100%" }}>
            <Box
              onClick={() => setExpandedId(isExpanded ? null : item.request_id)}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                cursor: "pointer",
                py: 1.5,
                px: 1,
                width: "100%",
                userSelect: "none",
              }}
            >
              <Typography
                variant="h6"
                color="#ffffff"
                textAlign="left"
                sx={{
                  fontSize: "15px",
                  fontWeight: "medium",
                }}
              >
                {title}
              </Typography>
              {/* Match ChevronRightIcon footprint from HistoryMenuMain (24px) */}
              <Box
                sx={{
                  position: "relative",
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  color: "#ffffff",
                }}
              >
                <AddIcon
                  sx={{
                    fontSize: "24px",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    opacity: isExpanded ? 0 : 1,
                    transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                    transition: "opacity 0.25s ease-in-out, transform 0.3s ease-in-out",
                  }}
                />
                <RemoveIcon
                  sx={{
                    fontSize: "24px",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    opacity: isExpanded ? 1 : 0,
                    transition: "opacity 0.25s ease-in-out",
                  }}
                />
              </Box>
            </Box>

            <Collapse in={isExpanded} timeout={300}>
              <Box
                sx={{
                  pb: 2,
                  pt: 0,
                  bgcolor: "#000000",
                  color: "#ffffff",
                  userSelect: "text",
                  WebkitUserSelect: "text",
                  cursor: "text",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <HistoryItemBody item={item} />
              </Box>
            </Collapse>

            {!isLast && (
              <Divider
                sx={{
                  width: "98%",
                  height: "1.5px",
                  bgcolor: "rgba(255, 255, 255, 0.3)",
                  my: 0,
                  mx: "auto",
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default HistoryEndpointDetail;
